import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ArrowUpDown, Download, FileArchive } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  downloadEdocta,
  downloadEdoctasZip,
  type EdoctaFilters,
  type EdoctaListItem,
  getCatalogos,
  listEdoctas,
} from "@/lib/api-edoctas";
import { formatDate, formatMXN } from "@/lib/utils";

const PAGE_SIZE = 50;

const keyOf = (r: EdoctaListItem) => `${r.clv_subcuenta}|${r.fecha_oper}`;

// Columnas ordenables (clave del backend → etiqueta y alineación).
const SORTABLE: { key: string; label: string; right?: boolean }[] = [
  { key: "fecha_oper", label: "Fecha oper." },
  { key: "sistema", label: "Sistema" },
  { key: "subcuenta", label: "Subcuenta" },
  { key: "archivo", label: "Archivo" },
  { key: "cargos", label: "Cargos", right: true },
  { key: "creditos", label: "Créditos", right: true },
  { key: "total_neto", label: "Total neto", right: true },
  { key: "descargado", label: "Descargado" },
];

export function EdoctasList() {
  const navigate = useNavigate();
  const [sistema, setSistema] = useState<string>("");
  const [subcuenta, setSubcuenta] = useState<string>("");
  const [desde, setDesde] = useState<string>("");
  const [hasta, setHasta] = useState<string>("");
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState("fecha_oper");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [zipping, setZipping] = useState(false);

  const handleSort = (k: string) => {
    if (sort === k) {
      setOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSort(k);
      setOrder(k === "fecha_oper" || k === "descargado" ? "desc" : "asc");
    }
    setPage(0);
  };

  const catalogos = useQuery({ queryKey: ["catalogos"], queryFn: getCatalogos });

  const filters: EdoctaFilters = useMemo(
    () => ({
      sistema: sistema || undefined,
      subcuenta: subcuenta || undefined,
      desde: desde || undefined,
      hasta: hasta || undefined,
      sort,
      order,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [sistema, subcuenta, desde, hasta, sort, order, page],
  );

  const lista = useQuery({
    queryKey: ["edoctas", filters],
    queryFn: () => listEdoctas(filters),
  });

  const totalPages = lista.data ? Math.ceil(lista.data.total / PAGE_SIZE) : 0;
  const items = lista.data?.items ?? [];
  const allSelected =
    items.length > 0 && items.every((r) => selected.has(keyOf(r)));

  const toggleOne = (r: EdoctaListItem) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const k = keyOf(r);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (items.every((r) => next.has(keyOf(r)))) {
        items.forEach((r) => next.delete(keyOf(r)));
      } else {
        items.forEach((r) => next.add(keyOf(r)));
      }
      return next;
    });
  };

  const onZip = async () => {
    if (selected.size === 0) return;
    setZipping(true);
    try {
      const seleccion = [...selected].map((k) => {
        const [subc, fecha] = k.split("|");
        return { subcuenta: subc, fecha };
      });
      await downloadEdoctasZip(seleccion);
    } catch (e) {
      alert(`Error al generar el ZIP: ${(e as Error).message}`);
    } finally {
      setZipping(false);
    }
  };

  const limpiarFiltros = () => {
    setSistema("");
    setSubcuenta("");
    setDesde("");
    setHasta("");
    setPage(0);
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border rounded-lg p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Sistema">
            <select
              className="border rounded px-2 py-1.5 w-full"
              value={sistema}
              onChange={(e) => {
                setSistema(e.target.value);
                setPage(0);
              }}
            >
              <option value="">Todos</option>
              {catalogos.data?.sistemas.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Subcuenta">
            <select
              className="border rounded px-2 py-1.5 w-full"
              value={subcuenta}
              onChange={(e) => {
                setSubcuenta(e.target.value);
                setPage(0);
              }}
            >
              <option value="">Todas</option>
              {catalogos.data?.subcuentas
                .filter((s) => !sistema || s.clv_sistema === sistema)
                .map((s) => (
                  <option key={s.clv_subcuenta} value={s.clv_subcuenta}>
                    {s.clv_subcuenta} ({s.clv_sistema})
                  </option>
                ))}
            </select>
          </Field>
          <Field label="Desde">
            <input
              type="date"
              className="border rounded px-2 py-1.5 w-full"
              value={desde}
              min={catalogos.data?.fecha_min ?? undefined}
              max={catalogos.data?.fecha_max ?? undefined}
              onChange={(e) => {
                setDesde(e.target.value);
                setPage(0);
              }}
            />
          </Field>
          <Field label="Hasta">
            <input
              type="date"
              className="border rounded px-2 py-1.5 w-full"
              value={hasta}
              min={catalogos.data?.fecha_min ?? undefined}
              max={catalogos.data?.fecha_max ?? undefined}
              onChange={(e) => {
                setHasta(e.target.value);
                setPage(0);
              }}
            />
          </Field>
        </div>

        <div className="flex items-center flex-wrap gap-x-3 gap-y-2">
          <button
            type="button"
            className="border rounded px-3 py-1.5 text-sm hover:bg-slate-50"
            onClick={limpiarFiltros}
          >
            Limpiar filtros
          </button>

          {lista.data && (
            <div className="text-sm text-slate-500">
              {lista.data.total.toLocaleString("es-MX")} facturas
              <span className="mx-1.5 text-slate-300">·</span>
              Cargos{" "}
              <b className="text-slate-700 tabular-nums">
                {formatMXN(lista.data.sum_cargos)}
              </b>
              <span className="mx-1.5 text-slate-300">·</span>
              Créditos{" "}
              <b className="text-slate-700 tabular-nums">
                {formatMXN(lista.data.sum_creditos)}
              </b>
              <span className="mx-1.5 text-slate-300">·</span>
              Total Neto{" "}
              <b className="text-slate-900 tabular-nums">
                {formatMXN(lista.data.sum_total_neto)}
              </b>
            </div>
          )}

          <button
            type="button"
            onClick={onZip}
            disabled={selected.size === 0 || zipping}
            className="ml-auto inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Descargar PDF y XML de las filas seleccionadas en un ZIP"
          >
            <FileArchive className={`w-4 h-4 ${zipping ? "animate-pulse" : ""}`} />
            {zipping ? "Generando ZIP…" : `Descargar ZIP (${selected.size})`}
          </button>
        </div>
      </div>

      <div className="bg-white border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-600 text-left">
            <tr>
              <th className="px-3 py-2 w-8">
                <input
                  type="checkbox"
                  className="cursor-pointer"
                  checked={allSelected}
                  onChange={toggleAll}
                  disabled={items.length === 0}
                  title="Seleccionar todo (esta página)"
                />
              </th>
              {SORTABLE.map((c) => (
                <th
                  key={c.key}
                  className={`px-3 py-2 font-medium select-none cursor-pointer hover:bg-slate-200 ${
                    c.right ? "text-right" : ""
                  }`}
                  onClick={() => handleSort(c.key)}
                >
                  <span className="inline-flex items-center gap-1">
                    {c.label}
                    <SortIcon active={sort === c.key} order={order} />
                  </span>
                </th>
              ))}
              <Th className="text-right">Acciones</Th>
            </tr>
          </thead>
          <tbody>
            {lista.isLoading && (
              <tr>
                <td colSpan={10} className="px-3 py-8 text-center text-slate-500">
                  Cargando…
                </td>
              </tr>
            )}
            {lista.isError && (
              <tr>
                <td colSpan={10} className="px-3 py-8 text-center text-red-600">
                  Error al cargar: {(lista.error as Error).message}
                </td>
              </tr>
            )}
            {lista.data?.items.length === 0 && (
              <tr>
                <td colSpan={10} className="px-3 py-8 text-center text-slate-500">
                  Sin resultados.
                </td>
              </tr>
            )}
            {items.map((row) => {
              const isSel = selected.has(keyOf(row));
              return (
                <tr
                  key={keyOf(row)}
                  className={`border-t cursor-pointer ${
                    isSel ? "bg-blue-50" : "hover:bg-slate-50"
                  }`}
                  onClick={() =>
                    navigate(`/reportes/edoctas/${row.clv_subcuenta}/${row.fecha_oper}`)
                  }
                >
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      className="cursor-pointer"
                      checked={isSel}
                      onChange={() => toggleOne(row)}
                    />
                  </td>
                  <Td>{formatDate(row.fecha_oper)}</Td>
                  <Td>{row.clv_sistema}</Td>
                  <Td>{row.clv_subcuenta}</Td>
                  <Td className="font-mono text-xs">{row.file_name ?? "—"}</Td>
                  <Td className="text-right tabular-nums">{formatMXN(row.cargos)}</Td>
                  <Td className="text-right tabular-nums">{formatMXN(row.creditos)}</Td>
                  <Td className="text-right tabular-nums font-medium">
                    {formatMXN(row.total_neto_dia)}
                  </Td>
                  <Td className="text-slate-500">{formatDate(row.descargado_en)}</Td>
                  <Td className="text-right">
                    <div className="inline-flex gap-2">
                      <DownloadBtn
                        onClick={() =>
                          downloadEdocta(row.clv_subcuenta, row.fecha_oper, "pdf")
                        }
                        label="PDF"
                      />
                      <DownloadBtn
                        onClick={() =>
                          downloadEdocta(row.clv_subcuenta, row.fecha_oper, "xml")
                        }
                        label="XML"
                      />
                    </div>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {lista.data && totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <div className="text-slate-500">
            {lista.data.total.toLocaleString("es-MX")} resultados · página{" "}
            {page + 1} de {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="border rounded px-3 py-1 disabled:opacity-50"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              ← Anterior
            </button>
            <button
              type="button"
              className="border rounded px-3 py-1 disabled:opacity-50"
              onClick={() => setPage((p) => p + 1)}
              disabled={page + 1 >= totalPages}
            >
              Siguiente →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-600">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Th({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <th className={`px-3 py-2 font-medium ${className}`}>{children}</th>;
}

function SortIcon({ active, order }: { active: boolean; order: "asc" | "desc" }) {
  if (!active) return <ArrowUpDown className="w-3 h-3 opacity-40" />;
  return order === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />;
}

function Td({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-3 py-2 ${className}`}>{children}</td>;
}

function DownloadBtn({ onClick, label }: { onClick: () => Promise<void>; label: string }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick().catch((err) => alert(`Error al descargar: ${err.message}`));
      }}
      className="inline-flex items-center gap-1 border rounded px-2 py-1 text-xs hover:bg-slate-100"
    >
      <Download className="w-3 h-3" />
      {label}
    </button>
  );
}
